import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  mobilePresentation?: 'dialog' | 'sheet' | 'fullscreen';
}

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  mobilePresentation,
}: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const previousOverflow = document.body.style.overflow;
    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const animationFrame = window.requestAnimationFrame(() => {
      const firstFocusable = panelRef.current?.querySelector<HTMLElement>(focusableSelector);
      (firstFocusable || panelRef.current)?.focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(focusableSelector)
      ).filter((element) => !element.hasAttribute('disabled') && element.offsetParent !== null);
      if (focusable.length === 0) {
        event.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.cancelAnimationFrame(animationFrame);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  const presentation = mobilePresentation ?? (size === 'xl' ? 'fullscreen' : 'dialog');
  const isSheet = presentation === 'sheet';
  const isFullscreen = presentation === 'fullscreen';

  const wrapperClasses = isSheet
    ? 'items-end justify-center md:items-center'
    : 'items-center justify-center';

  const mobilePanelClasses = isSheet
    ? 'mx-0 max-h-[85dvh] rounded-b-none rounded-t-[20px] border-x-0 border-b-0 md:mx-4 md:max-h-[90vh] md:rounded-2xl md:border'
    : isFullscreen
      ? 'mx-0 h-[100dvh] max-h-[100dvh] rounded-none border-0 md:mx-4 md:h-auto md:max-h-[90vh] md:rounded-2xl md:border'
      : 'mx-4 max-h-[90vh]';

  const overlayHost = document.querySelector<HTMLElement>('[data-portal-overlay-root]') ?? document.body;

  return createPortal(
    <div className={`fixed inset-0 z-50 flex ${wrapperClasses}`}>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm dark:bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`relative flex w-full flex-col bg-glass-4 backdrop-blur-2xl border border-glass rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.25)] text-glass-text ${sizeClasses[size]} ${mobilePanelClasses}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {isSheet && (
          <div className="flex h-5 shrink-0 items-center justify-center md:hidden" aria-hidden="true">
            <span className="h-1 w-10 rounded-full bg-glass-3" />
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between border-b border-glass p-4 md:p-6">
          <h2 id={titleId} className="text-base font-semibold text-glass-text md:text-xl">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-glass-text-2 transition-colors hover:bg-glass-3 hover:text-glass-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 md:h-9 md:w-9"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 glass-scroll md:p-6">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="rounded-b-2xl border-t border-glass bg-glass-1 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] md:p-6">
            {footer}
          </div>
        )}
      </div>
    </div>,
    overlayHost
  );
}
